import React from 'react'
import { SubmitHandler, useForm, Controller } from 'react-hook-form'
import { Task } from '@core/entities/task/Task'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { TaskAssignableUser } from '@core/entities/task/TaskAssignableUser'
import { useReassignTask } from '@core/react-query/features/tasks/task'
import { useNavigationContext } from '@common/hooks'
import { localizedStrings } from '@core/strings'
import {
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  TextField,
} from '@common/material'
import { Flex } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import ClearIcon from '@mui/icons-material/Clear'
import { TaskAssignUserPicker } from '@elements'
import { MAX_TASK_SUMMARY_CHARACTERS } from '@core/components/templateAnswersForm'

interface ComponentType {
  task: Task
}

type FormData = {
  assigneeUser: TaskAssignableUser
  notes: string
}

export const ReassignTaskModal: React.FC<ComponentType> = ({ task }) => {
  const { projectId } = useNavigationContext()
  const toast = useToast()
  const { hideModal } = useModal()
  const { control, handleSubmit } = useForm({
    defaultValues: {
      assigneeUser: null,
      notes: '',
    },
  })

  if (!task) return null

  const reassignTaskMutation = useReassignTask()

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    reassignTaskMutation.mutate(
      {
        id: task.id,
        assigneeUserId: data.assigneeUser.id,
        notes: data.notes,
      },
      {
        onSuccess: () => {
          toast.success('Task re-assigned successfully')
        },
        onError: (error) => {
          toast.error(error.message)
        },
        onSettled: () => {
          hideModal()
        },
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.reassignTask}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Flex flexDirection={'column'} sx={{ mt: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name='assigneeUser'
              rules={{ required: 'Please select an assignee' }}
              render={({ field, fieldState }) => (
                <TaskAssignUserPicker
                  projectId={projectId ?? task.project.id}
                  templateId={task.template.currentVersion.templateId}
                  stepId={
                    task.isDone() ? task.getLastStep().id : task.currentStep.id
                  }
                  value={field.value}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                  currentUser={task.assignee}
                />
              )}
            />

            <Controller
              name='notes'
              control={control}
              render={({ field: { onChange, ...field }, fieldState }) => {
                return (
                  <TextField
                    label={localizedStrings.note}
                    placeholder={localizedStrings.note}
                    data-test='note-input'
                    rows={3}
                    sx={{ mt: 2 }}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      `${
                        field.value?.length || 0
                      }/${MAX_TASK_SUMMARY_CHARACTERS} characters`
                    }
                    onChange={(e) =>
                      e.target.value.length <= MAX_TASK_SUMMARY_CHARACTERS &&
                      onChange(e)
                    }
                    slotProps={{
                      formHelperText: {
                        error: !!fieldState.error,
                        sx: { pt: 0.5, pb: 2 },
                      },
                    }}
                    fullWidth
                    multiline
                    {...field}
                  />
                )
              }}
            />

            <Flex gap={2} mt={4}>
              <Button variant='outlined' onClick={hideModal} fullWidth>
                {localizedStrings.cancel}
              </Button>
              <Button
                variant='contained'
                type='submit'
                fullWidth
                data-test='save-task'
                disabled={reassignTaskMutation.isLoading}
              >
                {localizedStrings.save}
              </Button>
            </Flex>
          </form>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
